import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Play, Share2, FileText } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { useEventCart } from "@/context/EventCartContext";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import ShareArtistDialog from "@/components/ShareArtistDialog";

function embedUrl(u) {
  if (!u) return null;
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = u.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return null;
}

export default function ArtistDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  const [showShare, setShowShare] = useState(false);
  const { addArtist } = useEventCart();
  const nav = useNavigate();

  useEffect(() => {
    api
      .get(`/artists/${id}`)
      .then((r) => setA(r.data))
      .catch((e) => setErr(formatApiError(e)));
  }, [id]);

  if (err) return <div className="pt-28 text-center text-[#A39E93]">{err}</div>;
  if (!a) return <div className="pt-28 text-center text-[#A39E93]">Caricamento…</div>;

  const embed = embedUrl(a.video_url);
  const gallery = a.gallery && a.gallery.length ? a.gallery : (a.image_url ? [a.image_url] : []);

  const requestJustHim = () => {
    addArtist(a);
    toast.success(`${a.name} aggiunto — vai in bozza per l'invio`);
    nav("/richiedi");
  };

  return (
    <div className="pt-28 pb-16">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/artisti"
          className="text-sm text-[#A39E93] hover:text-[#E5A93C] inline-flex items-center gap-2"
          data-testid="back-to-artists"
        >
          <ArrowLeft size={14} /> Torna al catalogo
        </Link>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 mt-6">
          <div>
            {a.image_url && (
              <div className="av-card overflow-hidden">
                <img src={a.image_url} alt={a.name} className="w-full aspect-[16/10] object-cover" />
              </div>
            )}
            {embed && (
              <div className="av-card overflow-hidden mt-5" data-testid="artist-video">
                <div className="aspect-video">
                  <iframe
                    src={embed}
                    title={a.name}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                  />
                </div>
              </div>
            )}
            {!embed && a.video_url && (
              <a
                href={a.video_url}
                target="_blank"
                rel="noreferrer"
                className="av-pill av-pill-ghost mt-5 inline-flex"
              >
                <Play size={14} /> Guarda il demo
              </a>
            )}

            {gallery.length > 1 && (
              <div className="grid grid-cols-3 gap-3 mt-5" data-testid="artist-gallery">
                {gallery.map((g, i) => (
                  <div key={i} className="av-card overflow-hidden">
                    <img src={g} alt="" className="w-full aspect-square object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <aside>
            <span className="av-eyebrow">{a.discipline}</span>
            <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 leading-tight">{a.name}</h1>
            <p className="text-[#A39E93] mt-2">{a.city}</p>
            <p className="text-[#F7F4EE]/80 mt-5 leading-relaxed whitespace-pre-line">{a.bio}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {(a.tags || []).map((t) => (
                <span key={t} className="text-xs px-2 py-1 rounded-full border av-hairline">
                  {t}
                </span>
              ))}
            </div>

            <div className="av-card p-5 mt-8">
              <div className="av-eyebrow">Proposta economica</div>
              <div className="font-serif-display text-3xl mt-2">
                {a.price_from ? `da € ${a.price_from.toLocaleString()}` : "Su richiesta"}
              </div>
              <p className="text-xs text-[#A39E93] mt-1">
                Il preventivo definitivo tiene conto di data, città e produzione.
              </p>
              <button
                onClick={requestJustHim}
                className="av-pill av-pill-gold w-full justify-center mt-5"
                data-testid="request-just-him-btn"
              >
                <Send size={14} /> Richiedi solo {a.name.split(" ")[0]}
              </button>
              <button
                onClick={() => {
                  addArtist(a);
                  toast.success(`${a.name} aggiunto alla bozza`);
                }}
                className="av-pill av-pill-ghost w-full justify-center mt-2"
                data-testid="add-to-draft-btn"
              >
                Aggiungi alla bozza evento
              </button>
              <button
                onClick={() => setShowShare(true)}
                className="av-pill av-pill-ghost w-full justify-center mt-2 text-sm"
                data-testid="open-share-btn"
              >
                <Share2 size={14} /> Condividi con QR / WhatsApp
              </button>
              <a
                href={`${API}/artists/${a.id}/tech-rider.pdf`}
                target="_blank"
                rel="noreferrer"
                className="av-pill av-pill-ghost w-full justify-center mt-2 text-sm"
                data-testid="tech-rider-pdf-btn"
              >
                <FileText size={14} /> Scarica scheda tecnica (PDF)
              </a>
            </div>
          </aside>
        </div>
      </section>
      {showShare && (
        <ShareArtistDialog
          artist={a}
          url={`${window.location.origin}/artisti/${a.id}`}
          onClose={() => setShowShare(false)}
        />
      )}
      <Footer />
    </div>
  );
}
